import { useLocalization } from "@fluent/react";
import { MenuButton } from "@narmi/design_system";
import type { FormattedBulkTemplate } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates/types";
import {
  useBulkTemplatePermissions,
  isMakePaymentDisabled,
} from "byzantine/src/dbbl/businessLogic/modules/bulkTemplates";

type TemplateState = FormattedBulkTemplate["state"];

type TemplateActionsMenuProps = {
  templateState: TemplateState;
  hasSendPayments: boolean;
  hasCollectPayments: boolean;
  editLoading?: boolean;
  onEdit: () => void;
  onMakePayment: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
};

/**
 * Actions available per template state:
 * - Pending approval: Duplicate, Archive
 * - Rejected: Duplicate, Archive
 * - Active: Edit, Make a payment, Duplicate, Archive
 * - Active (pending): Make a payment, Duplicate, Archive
 * - Archived: Duplicate
 */
const getActionsForState = (
  state: TemplateState
): {
  canEdit: boolean;
  canMakePayment: boolean;
  canDuplicate: boolean;
  canArchive: boolean;
} => {
  switch (state) {
    case "active":
      return {
        canEdit: true,
        canMakePayment: true,
        canDuplicate: true,
        canArchive: true,
      };
    case "active_pending":
      return {
        canEdit: false,
        canMakePayment: true,
        canDuplicate: true,
        canArchive: true,
      };
    case "pending_approval":
      return {
        canEdit: false,
        canMakePayment: false,
        canDuplicate: true,
        canArchive: true,
      };
    case "rejected":
      return {
        canEdit: true,
        canMakePayment: false,
        canDuplicate: true,
        canArchive: true,
      };
    case "archived":
      return {
        canEdit: false,
        canMakePayment: false,
        canDuplicate: true,
        canArchive: false,
      };
    default:
      return {
        canEdit: false,
        canMakePayment: false,
        canDuplicate: false,
        canArchive: false,
      };
  }
};

const TemplateActionsMenu = ({
  templateState,
  hasSendPayments,
  hasCollectPayments,
  editLoading,
  onEdit,
  onMakePayment,
  onDuplicate,
  onArchive,
}: TemplateActionsMenuProps) => {
  const { l10n } = useLocalization();

  const stateActions = getActionsForState(templateState);
  const permissions = useBulkTemplatePermissions();

  const showEdit = stateActions.canEdit;
  const showMakePayment = stateActions.canMakePayment;
  const showDuplicate = stateActions.canDuplicate;
  const showArchive = stateActions.canArchive;

  const editDisabled = !permissions.canEdit || Boolean(editLoading);
  const makePaymentDisabled = isMakePaymentDisabled(
    permissions,
    hasSendPayments,
    hasCollectPayments
  );
  const duplicateDisabled = !permissions.canDuplicate;
  const archiveDisabled = !permissions.canArchive;

  const hasAnyAction =
    (showMakePayment && !makePaymentDisabled) ||
    (showEdit && !editDisabled) ||
    (showArchive && !archiveDisabled) ||
    (showDuplicate && !duplicateDisabled);

  if (!hasAnyAction) return null;

  return (
    <MenuButton triggerIcon="more-vertical">
      {showMakePayment && !makePaymentDisabled && (
        <MenuButton.Item
          key="make-payment"
          label={l10n.getString("bulk-template-action-make-payment")}
          onSelect={onMakePayment}
        />
      )}
      {showEdit && !editDisabled && (
        <MenuButton.Item
          key="edit"
          label={l10n.getString("bulk-template-action-edit-template")}
          onSelect={onEdit}
        />
      )}
      {showArchive && !archiveDisabled && (
        <MenuButton.Item
          key="archive"
          label={l10n.getString("bulk-template-action-archive-template")}
          onSelect={onArchive}
        />
      )}
      {showDuplicate && !duplicateDisabled && (
        <MenuButton.Item
          key="duplicate"
          label={l10n.getString("bulk-template-action-copy-to-new-template")}
          onSelect={onDuplicate}
        />
      )}
    </MenuButton>
  );
};

export default TemplateActionsMenu;
